import type { JSX } from 'solid-js';
import { For, Match, Show, Switch } from 'solid-js';
import { t, tPlural } from '../i18n';
import { createPolicyLint, formatLintError, type PolicyLintError } from '../lib/policyLint';
import { canOwnerWrite } from '../lib/roles';
import { useMe } from '../lib/session';
import { Panel } from './Panel';
import { Button } from './ui/Button';

// Policy tab → "Re-check policy" (arc 101 slice 06): the only caller of
// `POST /v1/repos/:o/:r/policy/validate`. Re-runs today's schema + semantic
// rules over the ACTIVE snapshot — see `lib/policyLint.ts` for why that answer
// can differ from the verdict the ingest gave when the file landed.
//
// The verdict renders inline (a list of refusals is to be read, not toasted),
// and the two failure modes stay apart: the POLICY refusing is a list of
// problems, the CALL refusing is an outage.

export function PolicyLintPanel(props: {
  encoded: string;
  token: string | null;
  snapshot: Readonly<Record<string, unknown>>;
}): JSX.Element {
  const me = useMe(props.token);
  const lint = createPolicyLint({ encoded: () => props.encoded, token: props.token });
  // Mirrors `write:repos` (owner-only). Shut while `/v1/me` is in flight:
  // mid-load `role` is undefined, the same shape as the PAT / machine credential that
  // KEEPS the control, so the button would otherwise go live before we know who
  // is asking. `denied` is not `!allowed` — mid-load the write isn't refused,
  // it's unknown, and "owner action" would be a guess that flips a moment later.
  const allowed = (): boolean => !me.isLoading && canOwnerWrite(me.data?.role);
  const denied = (): boolean => !me.isLoading && !canOwnerWrite(me.data?.role);
  const running = (): boolean => lint.state().kind === 'running';
  const errors = (): readonly PolicyLintError[] => {
    const state = lint.state();
    return state.kind === 'errors' ? state.errors : [];
  };
  const failure = (): string | null => {
    const state = lint.state();
    return state.kind === 'failed' ? state.reason : null;
  };

  return (
    <Panel
      title={t('repo_detail.policy_lint_title')}
      footer={t('repo_detail.policy_lint_hint')}
      action={
        <Button
          variant="secondary"
          size="sm"
          disabled={running() || !allowed()}
          onClick={() => lint.run(props.snapshot)}
        >
          {running() ? t('repo_detail.policy_lint_running') : t('repo_detail.policy_lint')}
        </Button>
      }
    >
      <Switch>
        <Match when={lint.state().kind === 'ok'}>
          <p class="repo-policy__verdict" role="status">
            {t('repo_detail.policy_lint_ok')}
          </p>
        </Match>
        <Match when={errors().length > 0}>
          <div role="alert">
            <p class="repo-policy__verdict repo-policy__verdict--bad">
              {tPlural('repo_detail.policy_lint_errors', errors().length, {
                count: errors().length,
              })}
            </p>
            <ul class="repo-policy__errors">
              <For each={errors()}>{(e) => <li>{formatLintError(e)}</li>}</For>
            </ul>
          </div>
        </Match>
        <Match when={failure()}>
          {(reason) => (
            <p class="repo-policy__verdict repo-policy__verdict--bad" role="alert">
              {reason()}
            </p>
          )}
        </Match>
      </Switch>
      {/* A dead button with no reason reads as a bug; say why it's dead. */}
      <Show when={denied()}>
        <p class="repo-policy__note" role="status">
          {t('repo_detail.policy_lint_denied')}
        </p>
      </Show>
    </Panel>
  );
}
